import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import PageTitle from '../components/PageTitle';
import { useAuth } from '../context/AuthContext';

const SERVICES = [
  { icon: '📋', title: 'Income Tax Return', desc: 'Expert ITR filing for salaried, business, NRI, and capital gains cases. All ITR forms covered.', tag: 'Most Popular', link: '/register' },
  { icon: '🧾', title: 'GST Registration', desc: 'End-to-end GST registration for businesses. GSTIN in 3-7 days with full document support.', tag: 'Business', link: '/register' },
  { icon: '📊', title: 'GST Returns', desc: 'Monthly/quarterly GSTR-1, GSTR-3B filing and annual return. ITC reconciliation included.', tag: 'Business', link: '/register' },
  { icon: '🔐', title: 'Digital Signature Certificate', desc: 'Class 3, DGFT & Document Signer DSC for individuals and organisations. Delivered in 2-3 working days.', tag: 'DSC', link: '/register' },
];

const STEPS = [
  { num: '1', icon: '📝', title: 'Register & Submit', desc: 'Create your account, fill in your details and upload your documents securely.' },
  { num: '2', icon: '🔍', title: 'CA Reviews', desc: 'Our expert CA team reviews your documents and prepares your return or application.' },
  { num: '3', icon: '✅', title: 'You Approve', desc: 'We send you the draft for review. Approve it or request changes — your call.' },
  { num: '4', icon: '🏛', title: 'Filed & Done', desc: 'We file on your behalf and send you the acknowledgement. Simple as that.' },
];



export default function Home() {
  const { isLoggedIn, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn && isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isLoggedIn, isAdmin, navigate]);

  const [faqs, setFaqs]           = useState([]);
  const [form, setForm]           = useState({ fullName: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [openFaq, setOpenFaq]     = useState(null);
  const [settings, setSettings]   = useState({
    contact_phone: '+91 9411777195',
    support_email: 'info@mytaxniti.com',
    instagram_handle: 'mytaxniti',
    show_instagram: 'true',
    plan_1_title: 'Basic',
    plan_1_price: '499',
    plan_1_desc: 'For salaried individuals with simple returns.',
    plan_1_features: 'ITR-1 & ITR-2 filing\nForm 16 processing\nDeductions review\nEmail support',
    plan_2_title: 'Professional',
    plan_2_price: '1499',
    plan_2_desc: 'For business owners and capital gains cases.',
    plan_2_features: 'All ITR forms\nBusiness & capital gains\nTax planning advisory\nPhone + WhatsApp support\nFree notice handling',
    plan_3_title: 'NRI Premium',
    plan_3_price: '2999',
    plan_3_desc: 'Comprehensive for NRIs with foreign assets.',
    plan_3_features: 'All NRI ITR forms\nDTAA benefit optimization\nForeign asset reporting\nDedicated CA manager\nPriority 24hr support',
  });

  useEffect(() => {
    api.get('/api/faq/public').then(r => setFaqs(r.data)).catch(() => {});
    api.get('/api/settings/public').then(r => {
      if (r.data) {
        setSettings(prev => ({ ...prev, ...r.data }));
      }
    }).catch(() => {});
  }, []);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submitCallback = async e => {
    e.preventDefault();
    if (!form.fullName || !form.phone) return;
    setLoading(true);
    try {
      await api.post('/api/enquiry/submit', form);
      setSubmitted(true);
    } catch {
      alert('Failed to submit. Please try again.');
    } finally { setLoading(false); }
  };

  const plans = [
    {
      name: settings.plan_1_title,
      price: `₹${settings.plan_1_price}`,
      desc: settings.plan_1_desc,
      features: settings.plan_1_features ? settings.plan_1_features.split('\n').filter(Boolean) : [],
    },
    {
      name: settings.plan_2_title,
      price: `₹${settings.plan_2_price}`,
      desc: settings.plan_2_desc,
      features: settings.plan_2_features ? settings.plan_2_features.split('\n').filter(Boolean) : [],
      featured: true,
    },
    {
      name: settings.plan_3_title,
      price: `₹${settings.plan_3_price}`,
      desc: settings.plan_3_desc,
      features: settings.plan_3_features ? settings.plan_3_features.split('\n').filter(Boolean) : [],
    },
  ];

  return (
    <>
      <PageTitle title="Your Tax. Our Niti." />

      {/* ── HERO ── */}
      <section style={{ background: 'var(--navy)', minHeight: '90vh', display: 'flex', alignItems: 'center', padding: '4rem 1.25rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 70% at 70% 50%, rgba(201,146,42,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: 240, height: 240, borderRadius: '50%', border: '1px solid rgba(201,146,42,0.06)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%', position: 'relative' }}>

          {/* Brand */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '.1rem' }}>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(.85rem, 2vw, 1.1rem)', color: 'rgba(255,255,255,.4)', letterSpacing: '.25em', textTransform: 'uppercase', fontWeight: 400 }}>MY</span>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.2rem, 6vw, 4rem)', color: '#fff', fontWeight: 700, margin: '0 .15rem' }}>Tax</span>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.2rem, 6vw, 4rem)', color: 'var(--goldt)', fontWeight: 700 }}>नीति</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '.3rem' }}>
              <div style={{ height: 1, width: 30, background: 'rgba(201,146,42,.4)' }} />
              <span style={{ fontSize: 'clamp(.65rem, 2vw, .8rem)', color: 'rgba(255,255,255,.4)', letterSpacing: '.15em', textTransform: 'uppercase' }}>Your Tax Policy</span>
              <div style={{ height: 1, width: 30, background: 'rgba(201,146,42,.4)' }} />
            </div>
          </div>

          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 4vw, 2.8rem)', color: '#fff', lineHeight: 1.25, marginBottom: '1.1rem', maxWidth: 600 }}>
            Expert Tax Filing &<br />
            <span style={{ color: 'var(--goldt)' }}>CA Services</span> — Made Simple
          </h1>
          <p style={{ color: 'rgba(255,255,255,.55)', fontSize: 'clamp(.88rem, 2vw, 1.05rem)', marginBottom: '2rem', lineHeight: 1.75, maxWidth: 500 }}>
            ITR filing, GST, DSC, and business compliance — handled by qualified CAs. Your tax strategy, our expertise.
          </p>

          <div style={{ display: 'flex', gap: '.85rem', flexWrap: 'wrap' }}>
            <Link to="/register">
              <button style={{ background: 'var(--gold)', color: 'var(--navy)', border: 'none', borderRadius: 10, padding: '.85rem 1.75rem', fontFamily: 'DM Sans, sans-serif', fontSize: '.92rem', fontWeight: 700, cursor: 'pointer' }}>
                Get Started Free →
              </button>
            </Link>
            <a href="#callback">
              <button style={{ background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,.25)', borderRadius: 10, padding: '.85rem 1.5rem', fontFamily: 'DM Sans, sans-serif', fontSize: '.92rem', cursor: 'pointer' }}>
                Request Callback
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section style={{ background: 'var(--white)', padding: '4rem 1.25rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--gold)', fontWeight: 600, marginBottom: '.5rem' }}>Our Services</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.3rem)', color: 'var(--navy)' }}>Comprehensive Tax & CA Services</h2>
            <p style={{ color: 'var(--muted)', marginTop: '.6rem', fontSize: '.9rem' }}>Everything you need — ITR, GST, DSC, and more. All under one roof.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
            {SERVICES.map(s => (
              <div key={s.title}
                style={{ border: '1px solid var(--border)', borderRadius: 14, padding: '1.5rem', transition: 'all .2s', cursor: 'pointer', background: '#fff' }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(11,31,58,.09)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '.8rem' }}>{s.icon}</div>
                <div style={{ display: 'inline-block', background: 'var(--greenl)', color: 'var(--green)', fontSize: '.65rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '.18rem .55rem', borderRadius: 4, marginBottom: '.6rem' }}>{s.tag}</div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.05rem', color: 'var(--navy)', marginBottom: '.4rem' }}>{s.title}</h3>
                <p style={{ fontSize: '.85rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '.9rem' }}>{s.desc}</p>
                <Link to={s.link} style={{ color: 'var(--gold)', fontSize: '.83rem', fontWeight: 600, textDecoration: 'none' }}>Get Started →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ background: 'var(--navy)', padding: '4rem 1.25rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--goldt)', fontWeight: 600, marginBottom: '.5rem' }}>Simple Process</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.3rem)', color: '#fff' }}>How It Works</h2>
          </div>
          <div className="steps-grid">
            {STEPS.map(s => (
              <div key={s.num} style={{ textAlign: 'center', padding: '1.5rem 1rem', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 14 }}>
                <div style={{ width: 46, height: 46, background: 'var(--gold)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto .85rem', fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 700, color: 'var(--navy)' }}>{s.num}</div>
                <div style={{ fontSize: '1.3rem', marginBottom: '.6rem' }}>{s.icon}</div>
                <h4 style={{ fontFamily: 'Playfair Display, serif', color: '#fff', fontSize: '.95rem', marginBottom: '.4rem' }}>{s.title}</h4>
                <p style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.5)', lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section style={{ background: 'var(--white)', padding: '4rem 1.25rem' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--gold)', fontWeight: 600, marginBottom: '.5rem' }}>Pricing</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.3rem)', color: 'var(--navy)' }}>Simple, Transparent Pricing</h2>
            <p style={{ color: 'var(--muted)', marginTop: '.5rem', fontSize: '.88rem' }}>ITR Filing plans — GST, DSC & other services quoted separately</p>
          </div>
          <div className="pricing-grid">
            {plans.map((p, idx) => (
              <div key={p.name || idx} style={{ border: `${p.featured ? '2px solid var(--gold)' : '1px solid var(--border)'}`, borderRadius: 18, padding: '1.75rem', position: 'relative', background: p.featured ? '#fffdf8' : '#fff' }}>
                {p.featured && (
                  <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--gold)', color: 'var(--navy)', fontSize: '.7rem', fontWeight: 700, padding: '.2rem .8rem', borderRadius: 20, whiteSpace: 'nowrap' }}>Most Popular</div>
                )}
                <div style={{ fontSize: '.75rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.5rem' }}>{p.name}</div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', color: 'var(--navy)', marginBottom: '.25rem' }}>{p.price}<span style={{ fontSize: '.82rem', fontFamily: 'DM Sans, sans-serif', color: 'var(--muted)' }}> / return</span></div>
                <p style={{ fontSize: '.82rem', color: 'var(--muted)', marginBottom: '1.1rem' }}>{p.desc}</p>
                <ul style={{ listStyle: 'none', marginBottom: '1.4rem' }}>
                  {p.features.map(f => (
                    <li key={f} style={{ fontSize: '.83rem', padding: '.38rem 0', borderBottom: '1px solid var(--creamd)', display: 'flex', alignItems: 'center', gap: '.5rem', color: 'var(--text)' }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <button style={{ width: '100%', background: p.featured ? 'var(--navy)' : 'transparent', color: p.featured ? '#fff' : 'var(--navy)', border: `1.5px solid ${p.featured ? 'var(--navy)' : 'var(--border)'}`, borderRadius: 10, padding: '.7rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '.88rem', cursor: 'pointer' }}>
                    Get Started
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ background: 'var(--cream)', padding: '4rem 1.25rem' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--gold)', fontWeight: 600, marginBottom: '.5rem' }}>FAQs</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.3rem)', color: 'var(--navy)' }}>Frequently Asked Questions</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.65rem' }}>
            {faqs.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem', fontSize: '.9rem' }}>Loading FAQs...</div>
            )}
            {faqs.map(faq => (
              <div key={faq.id} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                <button onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                  style={{ width: '100%', background: 'none', border: 'none', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', fontWeight: 600, color: 'var(--navy)', textAlign: 'left', gap: '.75rem' }}>
                  <span>{faq.question}</span>
                  <span style={{ color: 'var(--gold)', fontSize: '1.1rem', flexShrink: 0 }}>{openFaq === faq.id ? '−' : '+'}</span>
                </button>
                {openFaq === faq.id && (
                  <div style={{ padding: '0 1.25rem 1rem', fontSize: '.86rem', color: 'var(--muted)', lineHeight: 1.7 }}>{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CALLBACK ── */}
      <section id="callback" style={{ background: 'var(--navy)', padding: '4rem 1.25rem' }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--goldt)', fontWeight: 600, marginBottom: '.5rem' }}>Free Consultation</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#fff', marginBottom: '.5rem' }}>Request a Callback</h2>
            <p style={{ color: 'rgba(255,255,255,.5)', fontSize: '.88rem' }}>Our CA experts will call you back within 2 hours.</p>
          </div>

          {submitted ? (
            <div style={{ background: 'rgba(26,107,67,.2)', border: '1px solid rgba(26,107,67,.4)', borderRadius: 14, padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
              <h3 style={{ color: '#fff', fontFamily: 'Playfair Display, serif', marginBottom: '.5rem' }}>Request Received!</h3>
              <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '.88rem' }}>We'll call you at <strong style={{ color: '#fff' }}>{form.phone}</strong> within 2 hours.</p>
            </div>
          ) : (
            <div style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, padding: '1.75rem' }}>
              <form onSubmit={submitCallback}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="field">
                    <label style={{ color: 'rgba(255,255,255,.7)' }}>Full Name<span className="req">*</span></label>
                    <input name="fullName" placeholder="Rahul Sharma" value={form.fullName} onChange={handle} required
                      style={{ background: 'rgba(255,255,255,.08)', border: '1.5px solid rgba(255,255,255,.15)', color: '#fff' }} />
                  </div>
                  <div className="field">
                    <label style={{ color: 'rgba(255,255,255,.7)' }}>Mobile Number<span className="req">*</span></label>
                    <input name="phone" placeholder="9876543210" value={form.phone} onChange={handle} required maxLength={10}
                      style={{ background: 'rgba(255,255,255,.08)', border: '1.5px solid rgba(255,255,255,.15)', color: '#fff' }} />
                  </div>
                  <div className="field">
                    <label style={{ color: 'rgba(255,255,255,.7)' }}>Service Needed</label>
                    <select name="service" value={form.service} onChange={handle}
                      style={{ 
                        background: 'rgba(30,50,80,.8)', 
                        border: '1.5px solid rgba(255,255,255,.15)', 
                        color: '#fff'
                      }}>
                      <option value="" style={{ background: 'var(--navy)', color: '#fff' }}>Select a service...</option>
                      <option value="ITR Filing" style={{ background: 'var(--navy)', color: '#fff' }}>ITR Filing</option>
                      <option value="GST Registration" style={{ background: 'var(--navy)', color: '#fff' }}>GST Registration</option>
                      <option value="GST Returns" style={{ background: 'var(--navy)', color: '#fff' }}>GST Returns</option>
                      <option value="DSC" style={{ background: 'var(--navy)', color: '#fff' }}>Digital Signature Certificate</option>
                      <option value="Tax Planning" style={{ background: 'var(--navy)', color: '#fff' }}>Tax Planning</option>
                      <option value="Other" style={{ background: 'var(--navy)', color: '#fff' }}>Other</option>
                    </select>
                  </div>
                  <div className="field">
                    <label style={{ color: 'rgba(255,255,255,.7)' }}>Message (optional)</label>
                    <textarea name="message" placeholder="Tell us briefly what you need help with..." rows={3} value={form.message} onChange={handle}
                      style={{ background: 'rgba(255,255,255,.08)', border: '1.5px solid rgba(255,255,255,.15)', color: '#fff', resize: 'vertical' }} />
                  </div>
                  <button type="submit" disabled={loading}
                    style={{ background: 'var(--gold)', color: 'var(--navy)', border: 'none', borderRadius: 10, padding: '.9rem', fontFamily: 'DM Sans, sans-serif', fontSize: '.92rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.5rem' }}>
                    {loading ? <><span className="spinner" /> Sending...</> : '📞 Request Callback'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#070F1E', borderTop: '2px solid var(--gold)', padding: '2.5rem 1.25rem 1.5rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="footer-grid" style={{ marginBottom: '1.75rem' }}>
            <div>
              <div style={{ marginBottom: '.75rem' }}>
                <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '.1rem' }}>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '.85rem', color: 'rgba(255,255,255,.35)', letterSpacing: '.2em' }}>MY</span>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.35rem', color: '#fff', fontWeight: 700 }}>Tax</span>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.35rem', color: 'var(--goldt)', fontWeight: 700 }}>Niti</span>
                </div>
                <div style={{ fontSize: '.65rem', color: 'rgba(255,255,255,.25)', letterSpacing: '.15em', marginTop: '.1rem' }}>नीति — YOUR TAX POLICY</div>
              </div>
            </div>
            <div>
              <div style={{ color: 'var(--goldt)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Services</div>
              {['ITR Filing', 'GST Registration', 'GST Returns', 'DSC'].map(s => (
                <div key={s} style={{ marginBottom: '.45rem' }}>
                  <Link to="/register" style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', textDecoration: 'none' }}>{s}</Link>
                </div>
              ))}
            </div>
            <div>
              <div style={{ color: 'var(--goldt)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Company</div>
              {[['Contact', '#callback'], ['Login', '/login'], ['Register', '/register']].map(([label, to]) => (
                <div key={label} style={{ marginBottom: '.45rem' }}>
                  <Link to={to} style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', textDecoration: 'none' }}>{label}</Link>
                </div>
              ))}
            </div>
            <div>
              <div style={{ color: 'var(--goldt)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Contact</div>
              <div style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', lineHeight: 2 }}>
                📞 {settings.contact_phone}<br />
                ✉️ {settings.support_email}<br />
                {settings.show_instagram === 'true' && settings.instagram_handle && (
                  <>
                    📸 <a href={`https://instagram.com/${settings.instagram_handle}`} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,.45)', textDecoration: 'none' }}>@{settings.instagram_handle}</a><br />
                  </>
                )}
                📍 Dehradun, Uttarakhand
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: '1.1rem', display: 'flex', justifyContent: 'space-between', fontSize: '.73rem', color: 'rgba(255,255,255,.25)', flexWrap: 'wrap', gap: '.5rem' }}>
            <span>© 2026 MyTaxNiti. All rights reserved.</span>
            <span style={{ background: 'rgba(255,255,255,.05)', padding: '.18rem .55rem', borderRadius: 4, border: '1px solid rgba(255,255,255,.1)' }}>SSL Secured</span>
          </div>
        </div>
      </footer>
    </>
  );
}